/**
 * Capa de datos del cruce de podios (port de cruce_local.py adaptado a los
 * aggregates servidos por Hosting).
 *
 * Fuentes (todas same-origin: el workflow las sincroniza al /public de
 * podios-stories; en dev las sirve el proxy de Vite):
 *   /agg_pbsb_<LETRA>.json     PB/SB por atleta+prueba (shards por inicial)
 *   /agg_pbsb_index.json       metadatos (temporada, letras)
 *   /minimas_internacional.json mínimas internacionales
 *   /RK<año>_200_AL|PC.json    ranking top-200 (líder nacional)
 *   recordsEspana.json         récords (bundle, import directo)
 *
 * Diferencia con cruce_local.py: el PB/SB viene YA calculado en el aggregate
 * (no el histórico con fechas), así que no se replica `_historial_antes_de`:
 * el PB del aggregate es el de la última carga semanal y, salvo que la
 * competición analizada ya esté en esa carga, no incluye la marca del día
 * (ver docs/plan-carga-semanal.md §2c, limitación conocida).
 */
import recordsData from '@/data/recordsEspana.json';
import { pruebaKey } from '@/lib/minimas';
import { stripDiacritics } from '@/lib/normalize';
import { resolveDataUrl } from '@/lib/dataManifest';
import {
  analizarResultado,
  etiquetasVideo,
  signaturaPrueba,
  claveInicial,
  clavesIniciales,
  categoriaPorEdad,
  valorMarca,
  vientoAnula,
  mejor,
  esConcurso,
  esCombinada,
  normaliza,
  anioDeFecha,
  type CruceOut,
  type MinimaCtx,
} from '@/lib/podios/cruce';

export const TEMPORADA_DEFECTO_YEAR = 2026;

/** Clave canónica de prueba — DEBE ser idéntica a build_aggregates.ts
 *  (buildPbSbShards): pruebaKey sobre la prueba sin género. */
function stripGenero(p: string): string {
  return (p || '')
    .replace(/\b(hombres?|mujeres?|varones|masculino|femenino|masc|fem)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
export function claveDePrueba(prueba: string): string {
  return pruebaKey(stripGenero(prueba));
}

function shardLetter(nombre: string): string {
  const c = stripDiacritics((nombre || '').trim().toLowerCase()).toUpperCase().charAt(0);
  return c >= 'A' && c <= 'Z' ? c : '#';
}

// ─────────────────────────── tipos de los aggregates ───────────────────────
interface AtletaPbSb {
  atleta: string;
  fechaNac: string;
  pais: string;
  ultimaTemporada: string;
  pb: Record<string, number>;
  sb: Record<string, number>;
}

interface MinimaItem {
  clave: string;
  sexo: 'H' | 'M';
  categoria: string;
  minima: string | null;
  label: string;
}

/** Índices del cruce, construidos para una competición concreta. */
export interface CruceIndices {
  year: number;
  // atletaNorm → {pb, sb, ...}; clave de prueba dentro de pb/sb.
  porAtleta: Map<string, AtletaPbSb>;
  // claveInicial → set de nombres normalizados (fallback rfealive.es).
  iniIdx: Map<string, Set<string>>;
  // nombre normalizado → nombre crudo BD (para mostrar canónico).
  nombreIdx: Map<string, string>;
  minimas: MinimaItem[];
  recordIdx: Map<string, number>;
  rankingAbs: Map<string, number>; // `${clave}|${sexoH}`
  rankingCat: Map<string, number>; // `${clave}|${sexoH}|${cat}`
  /** Fecha de generación de los agg_pbsb (ISO). Permite saber si las marcas
   *  de una competición YA están dentro de la BD (regla de Riki 2026-07-08:
   *  comparar contra el historial ANTERIOR al día de la competición; sin
   *  fecha por marca, esta es la mejor aproximación posible). */
  bdGeneratedAt: string | null;
  /** Top-10 histórico por `${fichero}|${sexo}|${clave}` (ver cargarAlltime). */
  alltime: AlltimeIdx;
}

// ─────────────────────────── fetch helpers ─────────────────────────────────
async function fetchJson<T>(path: string): Promise<T | null> {
  try {
    // Versionado por contenido (rama rankings-espana-advance 2026-07): los
    // datos-hoja (agg_pbsb_*, RK*_200_*) se sirven hasheados desde /d/<hash>.json
    // y el nombre lógico da 404. resolveDataUrl traduce por el data_manifest.json
    // (con fallback a la ruta lógica para ficheros no hasheados como alltime_*).
    const r = await fetch(await resolveDataUrl(path));
    if (!r.ok) return null;
    const ct = r.headers.get('content-type') || '';
    if (!ct.includes('json')) return null; // rewrite SPA → index.html
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

// ─────────────────────────── records (bundle) ──────────────────────────────
interface RecordRow {
  prueba: string; genero: string; categoria: string; indoor?: boolean; marca: string;
}
let _recordIdx: Map<string, number> | null = null;
function recordIndex(): Map<string, number> {
  if (_recordIdx) return _recordIdx;
  const idx = new Map<string, number>();
  const rows = (recordsData as { records: RecordRow[] }).records || [];
  for (const r of rows) {
    const key = `${normaliza(r.prueba)}|${r.genero}|${r.categoria}|${r.indoor ? '1' : '0'}`;
    if (!idx.has(key)) {
      const v = valorMarca(r.marca);
      if (v !== null) idx.set(key, v);
    }
  }
  _recordIdx = idx;
  return idx;
}
// ─────────────────── all-time top-10 por categoría ─────────────────────────
// PDFs de ranking histórico RFEA (AEEA/Miguel Villaseñor) parseados a
// /public/alltime_*.json con parse_alltime_pdf.py (podios-stories/,
// 2026-07-08). Cada atleta se compara contra la lista de SU categoría; Sub
// 10/12 usan la sub-14 (no hay lista propia) y Absoluta/Master van por
// sexo. Se OMITEN las combinadas ("Pruebas: …", "( … )") y las variantes
// "short track" del PDF: su clave colisionaría con la prueba normal
// ("200 metros short track" → "200m") y mezclaría listas distintas.
interface AlltimeEntry { marca: string; valor: number; atleta: string; fecha_nac?: string }
export type AlltimeIdx = Map<string, { valor: number; atletaNorm: string; fechaNac: string | null }[]>;
const ALLTIME_FICHEROS: Record<string, string> = {
  'Sub 10': 'alltime_sub14.json', 'Sub 12': 'alltime_sub14.json', 'Sub 14': 'alltime_sub14.json',
  'Sub 16': 'alltime_sub16.json', 'Sub 18': 'alltime_sub18.json',
  'Sub 20': 'alltime_sub20.json', 'Sub 23': 'alltime_sub23.json',
};
function ficheroAlltime(cat: string | null, sexoH: 'H' | 'M' | null): string | null {
  if (!cat || !sexoH) return null;
  if (cat in ALLTIME_FICHEROS) return ALLTIME_FICHEROS[cat];
  if (cat === 'Absoluta' || cat === 'Master') return sexoH === 'H' ? 'alltime_abs-hombres.json' : 'alltime_abs-mujeres.json';
  return null;
}
async function cargarAlltime(): Promise<AlltimeIdx> {
  const idx: AlltimeIdx = new Map();
  const ficheros = [...new Set([...Object.values(ALLTIME_FICHEROS), 'alltime_abs-hombres.json', 'alltime_abs-mujeres.json'])];
  await Promise.all(ficheros.map(async (fname) => {
    const doc = await fetchJson<Record<string, Record<string, AlltimeEntry[]>>>(`/${fname}`);
    if (!doc) return;
    for (const [sexoRaw, sexo] of [['hombres', 'H'], ['mujeres', 'M']] as const) {
      for (const [rawPrueba, entradas] of Object.entries(doc[sexoRaw] ?? {})) {
        const low = rawPrueba.toLowerCase();
        const esComb = esCombinada(rawPrueba);
        // "short track" se salta SOLO en pruebas normales (su clave chocaría con
        // la de aire libre: "200 m ST" → "200m"). En COMBINADAS no choca (indoor
        // y aire libre son pruebas distintas por sexo: hept/pentatlón indoor vs
        // deca/heptatlón aire libre), así que se INCLUYE, quitándole el "short
        // track" para que su clave case con la del evento en vivo (Riki
        // 2026-07-14; antes el all-time de combinadas indoor no salía).
        if ((!esComb && low.includes('short track')) || rawPrueba.includes('(') || low.startsWith('pruebas')) continue;
        const nombreClave = esComb ? rawPrueba.replace(/short track/gi, ' ').replace(/\s+/g, ' ').trim() : rawPrueba;
        const clave = claveDePrueba(nombreClave.replace(/\bmetros\b/gi, 'm'));
        if (!clave) continue;
        idx.set(`${fname}|${sexo}|${clave}`, (entradas ?? [])
          .filter((e) => typeof e.valor === 'number')
          .map((e) => ({ valor: e.valor, atletaNorm: normaliza(e.atleta), fechaNac: e.fecha_nac ?? null })));
      }
    }
  }));
  return idx;
}

function buscarRecord(idx: Map<string, number>, basePrueba: string, sexo: 'H' | 'M', categoria: string | null, indoor: boolean): number | null {
  if (!categoria) return null;
  const genero = sexo === 'H' ? 'Hombre' : 'Mujer';
  const key = `${normaliza(basePrueba)}|${genero}|${categoria}|${indoor ? '1' : '0'}`;
  return idx.get(key) ?? null;
}

// ─────────────────────────── mínimas internacionales ───────────────────────
const NIVEL: Record<string, number> = {
  'Sub 8': 8, 'Sub 10': 10, 'Sub 12': 12, 'Sub 14': 14, 'Sub 16': 16,
  'Sub 18': 18, 'Sub 20': 20, 'Sub 23': 23, 'Absoluta': 100, 'Master': 35,
};
function elegible(catAtleta: string | null, catMinima: string): boolean {
  if (!catAtleta) return false;
  if (catMinima === 'Absoluta') return (NIVEL[catAtleta] ?? 0) >= 16;
  return catAtleta === catMinima;
}

interface MinimasDoc {
  competiciones: { competicion: string; label: string; minimas: { prueba: string; sexo: 'H' | 'M'; categoria: string; minimaRfea: string }[] }[];
}
async function cargarMinimas(): Promise<MinimaItem[]> {
  const doc = await fetchJson<MinimasDoc>('/minimas_internacional.json');
  if (!doc) return [];
  const out: MinimaItem[] = [];
  for (const c of doc.competiciones || []) {
    for (const e of c.minimas || []) {
      out.push({
        clave: claveDePrueba(e.prueba),
        sexo: e.sexo,
        categoria: e.categoria || 'Absoluta',
        minima: e.minimaRfea ?? null,
        label: c.label || c.competicion,
      });
    }
  }
  return out;
}
function minimasAplicables(minimas: MinimaItem[], claveR: string, sexoH: 'H' | 'M', catEdad: string | null): MinimaCtx[] {
  const out: MinimaCtx[] = [];
  for (const m of minimas) {
    if (m.clave !== claveR || m.sexo !== sexoH) continue;
    if (!elegible(catEdad, m.categoria)) continue;
    out.push({ minima: m.minima, minimaRepesca: null, label: `${m.label} ${m.categoria}` });
  }
  return out;
}

// ─────────────────────────── ranking (RK200) ───────────────────────────────
interface Rk200Doc {
  year: number;
  pista: string;
  groups: Record<string, [number, string, string, string, number, string, number][]>;
}
/** Construye rankingAbs/rankingCat del año desde RK200 AL + PC. */
async function cargarRanking(year: number): Promise<{ abs: Map<string, number>; cat: Map<string, number> }> {
  const abs = new Map<string, number>();
  const cat = new Map<string, number>();
  for (const pista of ['AL', 'PC']) {
    const doc = await fetchJson<Rk200Doc>(`/RK${year}_200_${pista}.json`);
    if (!doc?.groups) continue;
    for (const [gkey, filas] of Object.entries(doc.groups)) {
      const [pruebaRaw, , genero] = gkey.split('|'); // prueba|categoria|genero|pista
      const categoria = gkey.split('|')[1];
      const clave = claveDePrueba(pruebaRaw);
      if (!clave) continue;
      const sexoH = genero === 'Hombre' ? 'H' : genero === 'Mujer' ? 'M' : null;
      if (!sexoH) continue;
      const concurso = esConcurso(pruebaRaw);
      for (const fila of filas) {
        const marca = fila[1];
        const viento = fila[2];
        if (vientoAnula(viento)) continue;
        const v = valorMarca(marca);
        if (v === null) continue;
        const kAbs = `${clave}|${sexoH}`;
        if (mejor(v, abs.get(kAbs) ?? null, concurso)) abs.set(kAbs, v);
        if (categoria) {
          const kCat = `${clave}|${sexoH}|${categoria}`;
          if (mejor(v, cat.get(kCat) ?? null, concurso)) cat.set(kCat, v);
        }
      }
    }
  }
  return { abs, cat };
}

// ─────────────────────────── PB/SB shards ──────────────────────────────────
/** Mezcla src en dest quedándose con la mejor marca por prueba (ver el
 *  comentario sobre atletas duplicados en cargarShards). */
function fusionarMarcas(dest: Record<string, number>, src: Record<string, number>): void {
  for (const [clave, v] of Object.entries(src)) {
    const actual = clave in dest ? dest[clave] : null;
    if (actual === null || mejor(v, actual, esConcurso(clave))) dest[clave] = v;
  }
}

async function cargarShards(iniciales: Set<string>): Promise<{
  porAtleta: Map<string, AtletaPbSb>; iniIdx: Map<string, Set<string>>; nombreIdx: Map<string, string>;
  generatedAt: string | null;
}> {
  const porAtleta = new Map<string, AtletaPbSb>();
  const iniIdx = new Map<string, Set<string>>();
  const nombreIdx = new Map<string, string>();
  let generatedAt: string | null = null;
  await Promise.all(
    [...iniciales].map(async (letra) => {
      const doc = await fetchJson<{ generatedAt?: string; atletas: AtletaPbSb[] }>(`/agg_pbsb_${letra}.json`);
      if (!doc?.atletas) return;
      if (doc.generatedAt && (!generatedAt || doc.generatedAt > generatedAt)) generatedAt = doc.generatedAt;
      for (const a of doc.atletas) {
        const norm = normaliza(a.atleta);
        const previo = porAtleta.get(norm);
        if (!previo) {
          porAtleta.set(norm, a);
          nombreIdx.set(norm, a.atleta);
        } else {
          // La BD trae a veces el MISMO atleta duplicado con dos grafías
          // que normalizan igual (caso real 2026-07-08 con Riki: "Nicolas
          // Ceballos Muñagorri" solo con el 4x100 del relevo y "NICOLAS
          // CEBALLOS MUÑAGORRI" con todo su historial). Antes ganaba la
          // primera entrada del shard — si era la vacía, el cruce no le
          // sacaba ni PB ni récord (su récord de España sub-18 de 110 m
          // vallas quedaba invisible). Ahora se FUSIONAN: mejor marca por
          // prueba, y el nombre de la entrada con más historial.
          if (Object.keys(a.pb).length > Object.keys(previo.pb).length) {
            nombreIdx.set(norm, a.atleta);
            previo.fechaNac = previo.fechaNac || a.fechaNac;
          }
          fusionarMarcas(previo.pb, a.pb);
          fusionarMarcas(previo.sb, a.sb);
        }
        for (const ik of clavesIniciales(norm)) {
          let s = iniIdx.get(ik);
          if (!s) { s = new Set(); iniIdx.set(ik, s); }
          s.add(norm);
        }
      }
    }),
  );
  return { porAtleta, iniIdx, nombreIdx, generatedAt };
}

/** Timestamp (UTC, solo fecha) de un string tipo '2026-07-06T…', '27/06/2026'
 *  o '27-6-2026'; null si no se reconoce. */
function fechaTs(s: string | null | undefined): number | null {
  if (!s) return null;
  let m = String(s).match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]);
  m = String(s).match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (m) return Date.UTC(+m[3], +m[2] - 1, +m[1]);
  return null;
}

// ─────────────────────────── API pública ───────────────────────────────────
export interface FilaPodio {
  nombre?: string | null;
  atleta?: string | null;
  pais?: string | null;
  prueba: string;
  marca?: string | null;
  fechaNac?: string | null;
  viento?: string | null;
  fecha?: string | null;
  lugar?: string | null;
}

/** Construye los índices necesarios para cruzar un conjunto de filas:
 *  descarga solo los shards de las iniciales presentes + records + minimas
 *  + ranking del año. */
export async function construirIndices(filas: FilaPodio[], year = TEMPORADA_DEFECTO_YEAR): Promise<CruceIndices> {
  const iniciales = new Set<string>();
  for (const f of filas) {
    const nombre = f.atleta || f.nombre || '';
    if (nombre) iniciales.add(shardLetter(nombre));
  }
  const [{ porAtleta, iniIdx, nombreIdx, generatedAt }, minimas, ranking, alltime] = await Promise.all([
    cargarShards(iniciales),
    cargarMinimas(),
    cargarRanking(year),
    cargarAlltime(),
  ]);
  return {
    year, porAtleta, iniIdx, nombreIdx, minimas,
    recordIdx: recordIndex(), rankingAbs: ranking.abs, rankingCat: ranking.cat,
    bdGeneratedAt: generatedAt,
    alltime,
  };
}

function resolverNombre(aNorm: string, idx: CruceIndices): string {
  if (idx.porAtleta.has(aNorm)) return aNorm;
  const candidatos = idx.iniIdx.get(claveInicial(aNorm));
  if (candidatos && candidatos.size === 1) return [...candidatos][0];
  return aNorm;
}

function sexoDe(f: FilaPodio): 'H' | 'M' | null {
  const sig = signaturaPrueba(f.prueba);
  return sig.sexo;
}

export interface ResultadoAnalizado {
  out: CruceOut;
  etiquetas: string[];
}

/** Analiza una competición: por cada fila devuelve el CruceOut con flags +
 *  las etiquetas a pintar. Mantiene el orden de entrada. */
export function analizarCompeticion(filas: FilaPodio[], idx: CruceIndices): ResultadoAnalizado[] {
  const res: ResultadoAnalizado[] = [];
  // Candidatos al top-10 histórico de su categoría (se resuelven en la 2ª
  // pasada, porque TODAS las marcas nuevas de la competición entran JUNTAS
  // en el ranking — regla de Riki 2026-07-08). Las etiquetas se calculan al
  // FINAL: el flag ALLTIME debe existir antes de generar las píldoras.
  const candidatos: { i: number; grupo: string; v: number; concurso: boolean; aNorm: string; fechaNac: string | null }[] = [];
  // Para la 3ª pasada (mínimas con consistencia de podio): datos por fila.
  const metas: { i: number; clave: string; sexoH: 'H' | 'M' | null; cat: string | null; concurso: boolean; sb: number | undefined }[] = [];
  for (const f of filas) {
    const clave = claveDePrueba(f.prueba);
    const sexoH = sexoDe(f);
    let aNorm = normaliza(f.atleta || f.nombre || '');
    aNorm = resolverNombre(aNorm, idx);
    const atleta = idx.porAtleta.get(aNorm);
    // Categoría por edad: rfealive.es NO trae fechaNac en los resultados
    // (limitación conocida de esa fuente) → cat quedaba null y buscarRecord
    // / minimasAplicables / rankingMejorCat se saltaban enteros (caso real
    // 2026-07-08 con Riki: el récord de España sub-18 de 110 m vallas de
    // Ceballos era invisible porque nunca se consultaba el récord de SU
    // categoría). La BD sí conoce al atleta: su fechaNac sirve de respaldo.
    const cat = categoriaPorEdad(anioDeFecha(f.fechaNac ?? atleta?.fechaNac), idx.year);
    const indoor = (f.lugar || '').toLowerCase().includes('(i)');
    const concurso = esConcurso(f.prueba);

    const pb = atleta?.pb[clave];
    const sb = atleta?.sb[clave];
    // ¿Las marcas de ESTA competición ya están dentro de la BD semanal?
    // Si la competición es anterior (o igual) a la fecha de generación de
    // los agg_pbsb, sí: un "empate" con el PB/SB significa casi seguro que
    // el PB/SB ES la marca de este día → se muestra PB/SB, no =PB/=SB
    // (regla de Riki 2026-07-08). Si la competición es posterior (en
    // directo), la BD no puede contenerla y el empate es real.
    const tsComp = fechaTs(f.fecha);
    const tsBD = fechaTs(idx.bdGeneratedAt);
    const bdIncluyeCompeticion = tsComp !== null && tsBD !== null && tsComp <= tsBD;
    const ctxOut = analizarResultado(
      {
        atleta: idx.nombreIdx.get(aNorm) || f.nombre || f.atleta,
        prueba: f.prueba, marca: f.marca, viento: f.viento, pais: f.pais, fechaNac: f.fechaNac,
      },
      {
        temporada: idx.year,
        historial: pb != null ? [pb] : [],
        historialTemporada: sb != null ? [sb] : [],
        minimas: sexoH ? minimasAplicables(idx.minimas, clave, sexoH, cat) : [],
        recordValor: buscarRecord(idx.recordIdx, signaturaPrueba(f.prueba).base, sexoH || 'H', cat, indoor),
        rankingMejorAbs: sexoH ? (idx.rankingAbs.get(`${clave}|${sexoH}`) ?? null) : null,
        rankingMejorCat: sexoH && cat ? (idx.rankingCat.get(`${clave}|${sexoH}|${cat}`) ?? null) : null,
        bdIncluyeCompeticion,
      },
    );
    ctxOut.categoriaEdad = cat;
    res.push({ out: ctxOut, etiquetas: [] });
    metas.push({ i: res.length - 1, clave, sexoH, cat, concurso, sb });

    // ¿Candidato al top-10 histórico de su categoría? Marca válida, viento
    // legal, no extranjero, y con lista all-time para su cat/sexo/prueba.
    const v = valorMarca(f.marca);
    const fichero = ficheroAlltime(cat, sexoH);
    const grupo = fichero && sexoH ? `${fichero}|${sexoH}|${clave}` : null;
    if (v !== null && !ctxOut.vientoAnula && !ctxOut.extranjero && grupo && idx.alltime.has(grupo)) {
      candidatos.push({ i: res.length - 1, grupo, v, concurso, aNorm, fechaNac: f.fechaNac ?? atleta?.fechaNac ?? null });
    }
  }

  // ── 2ª pasada: posición all-time por atleta (regla de Riki 2026-07-09, ────
  // ajustada 2026-07-11) ─
  // La lista efectiva (`efectivas`) se construye con el MEJOR entre la marca
  // histórica y la de hoy de cada atleta del podio, para que cada uno ocupe su
  // puesto real. El atleta se empareja con su entrada del histórico por FECHA DE
  // NACIMIENTO (el nombre es frágil: el PDF lista "Nombre Apellido1" y el cruce
  // resuelve al nombre de la BD, a veces con 2 apellidos → no casaban).
  // Posición = 1 + nº de marcas estrictamente mejores; los empates comparten
  // puesto.
  //
  // La etiqueta "#N all-time", en cambio, se calcula sobre la marca de HOY: solo
  // se muestra si la marca de ESTE DÍA entra en el top-10. Tener un PB top-10 no
  // basta (p. ej. una 3ª que hoy queda lejos de su PB no se etiqueta, aunque su
  // PB sea #7). Además, se emite SOLO si alguien del podio MEJORA su estatus ese
  // día (entra nuevo o bate su marca previa), para no etiquetar cuando nadie
  // hace nada all-time.
  if (candidatos.length) {
    const porGrupo = new Map<string, typeof candidatos>();
    for (const c of candidatos) {
      const g = porGrupo.get(c.grupo);
      if (g) g.push(c); else porGrupo.set(c.grupo, [c]);
    }
    for (const [grupo, cands] of porGrupo) {
      const hist = idx.alltime.get(grupo);
      if (!hist?.length) continue;
      const concurso = cands[0].concurso;

      // Identidad del atleta = fecha de nacimiento si la hay, si no el nombre.
      // Su MEJOR marca del día (semis+final del mismo atleta no puntúan doble).
      type Atl = { fechaNac: string | null; aNorm: string; v: number; idxs: number[] };
      const porAtleta = new Map<string, Atl>();
      for (const c of cands) {
        const ts = fechaTs(c.fechaNac);
        const key = ts !== null ? `dob:${ts}` : `nom:${c.aNorm}`;
        const a = porAtleta.get(key);
        if (!a) porAtleta.set(key, { fechaNac: c.fechaNac, aNorm: c.aNorm, v: c.v, idxs: [c.i] });
        else if (mejor(c.v, a.v, concurso)) { a.v = c.v; a.idxs = [c.i]; }
        else if (c.v === a.v) a.idxs.push(c.i);
      }
      const atletas = [...porAtleta.values()];

      // Entrada del atleta en el histórico: por fecha de nacimiento, o por nombre.
      const histEntryDe = (a: Atl) => {
        const ts = fechaTs(a.fechaNac);
        if (ts !== null) {
          const e = hist.find((h) => fechaTs(h.fechaNac) === ts);
          if (e) return e;
        }
        return hist.find((h) => h.atletaNorm === a.aNorm) ?? null;
      };
      const suEntrada = atletas.map(histEntryDe);
      const propias = new Set(suEntrada.filter(Boolean));
      const mejorDeCadaUno = atletas.map((a, k) => {
        const e = suEntrada[k];
        return e ? (mejor(a.v, e.valor, concurso) ? a.v : e.valor) : a.v;
      });
      // Marcas efectivas del ranking: histórico SIN las entradas de los del
      // podio (sustituidas por su mejor marca) + la mejor marca de cada uno.
      const efectivas = [
        ...hist.filter((e) => !propias.has(e)).map((e) => e.valor),
        ...mejorDeCadaUno,
      ];
      const posDe = (val: number) => 1 + efectivas.filter((x) => mejor(x, val, concurso)).length;

      // ¿Alguien del podio MEJORA su estatus all-time hoy Y acaba en top-10?
      const info = atletas.map((a, k) => {
        const e = suEntrada[k];
        // Posición y elegibilidad SOLO por la marca de HOY (a.v), no por la
        // mejor histórica (regla de Riki 2026-07-11): tener un PB top-10 no
        // basta; si la marca de este día no entra en el top-10, no se etiqueta.
        // Caso real: Sofía Cosculluela, PB 6.52 = #7 all-time, pero hoy 6.38 no
        // es top-10 → sin etiqueta. El histórico (mejorDeCadaUno) sigue usándose
        // en `efectivas` para que cada atleta ocupe su puesto real en la lista.
        const pos = posDe(a.v);
        return { a, pos, enTop10: pos <= 10, mejora: e ? mejor(a.v, e.valor, concurso) : true };
      });
      if (!info.some((x) => x.mejora && x.enTop10)) continue; // nadie hace nada all-time -> sin etiquetas

      // #N real a todo top-10 (ganadora ya clasificada incluida). El flag lo
      // lee etiquetasVideo() y emite "#N all-time". Paridad con _anotar_alltime.
      for (const { a, pos, enTop10 } of info) {
        if (enTop10) for (const i of a.idxs) res[i].out.flags.push(['ALLTIME', String(pos)]);
      }
    }
  }

  // (La antigua 3ª pasada de "consistencia de podio" —que repartía la mínima a
  // otros del podio por su SB— se eliminó el 2026-07-21: la mínima solo sale si
  // la marca de HOY de ESE atleta es PB/SB y la cumple, no por lo que hagan sus
  // rivales. La distinción nueva/mejora ya la resuelve analizarResultado.)

  for (const r of res) r.etiquetas = etiquetasVideo(r.out);
  return res;
}
